/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { FixedSizeList as List } from "react-window";
import {
    TextField as FluentTextField,
    Label,
} from '@fluentui/react';
import useAxios from 'axios-hooks';
import GoogleAnalyticsDataPane from "./GoogleAnalyticsDataPane";
import GoogleAnalyticsMesageBar from "./GoogleAnalyticsMessageBar";
import GoogleAnlyticsNotifications from "./GoogleAnalyticsNotifications";

const container = css`
    .form-group {
        margin-top: 5px;
        margin-bottom: 5px;
    }
    .container-body{
    max-width: 600px;
    margin: auto;
    .ms-TextField, .ms-Dropdown, .ms-ChoiceGroup-field {
        margin-bottom: 15px;
    }
    h2, h3 {
        margin-bottom: 20px;
    }
    }
`;

const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        padding: "0 8px",
                        boxSizing: "border-box",
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

// Custom TextField to integrate Fluent UI with Formik
const FormikTextField = ({ field, form, label, ...props }) => {
    const { name, value } = field;
    const { setFieldValue } = form;
    const errorMessage = form.touched[name] && form.errors[name] ? form.errors[name] : "";
    return (
        <FluentTextField
            {...props}
            label={label}
            name={name}
            value={value}
            onChange={(e, newValue) => setFieldValue(name, newValue)}
            onBlur={field.onBlur}
            errorMessage={errorMessage}
        />
    );
};

FormikTextField.propTypes = {
    field: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.any,
        onBlur: PropTypes.func.isRequired,
    }).isRequired,
    form: PropTypes.shape({
        touched: PropTypes.object.isRequired,
        errors: PropTypes.object.isRequired,
        setFieldValue: PropTypes.func.isRequired,
    }).isRequired,
    label: PropTypes.string.isRequired,
};

const GoogleAnalyticsPage = () => {
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("Saved");
    const [gaAccountOptions, setGaAccountOptions] = useState([]);
    const [ga4ValidationStatus, setGa4ValidationStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
    const [ga4OverallMessage, setGa4OverallMessage] = useState('');
    const [ga4DetailedErrors, setGa4DetailedErrors] = useState([]);
    const [generationStatus, setGenerationStatus] = useState({ message: '', type: null });
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedPropertyForEdit, setSelectedPropertyForEdit] = useState(null);
    const [editInitialValues, setEditInitialValues] = useState(null);
    const [displayedGa4Properties, setDisplayedGa4Properties] = useState([]);
    const [cobrandId, setCobrandId] = useState([]);

    const [{ data: cobrandsData, loading: loadingCobrands }] = useAxios('/internal/cobrands?bustCache=true');
    const [{ data: clients}] = useAxios('/internal/clients?bustCache=true'); // Added back client fetching
    const [{ data: fetchedGaAccountsData, loading: gaAccountsLoadingState, error: gaAccountsErrorState }, fetchAccounts] = useAxios(
        { url: '/google-analytics/accounts', method: 'GET' },
        { manual: true }
    );

    const [{loading: loadingCobrandSettings}, fetchCobrandSettings] = useAxios(
        { url: '/internal/cobrand/launcher', method: 'GET' },
        { manual: true }
    );

    const [{ loading: validatingGa4, error: validateGa4ApiError }, executeValidateGa4] = useAxios(
        { url: `/google-analytics/validate-cobrand`, method: 'GET' },
        { manual: true }
    );

    const [{ data: allGa4PropertiesData, loading: loadingAllGa4Properties, error: errorAllGa4Properties }, fetchAllGa4Properties] = useAxios(
        { url: '/google-analytics/all', method: 'GET' },
        { manual: true }
    );

    const [{ loading: loadingSetupGa }, setupGa] = useAxios(
        { url: `/google-analytics/setup`, method: 'POST' },
        { manual: true },
    );

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    useEffect(() => {
        if (gaAccountsLoadingState === false) {
            if (gaAccountsErrorState) {
                console.error("Error fetching GA accounts:", gaAccountsErrorState);
                setGaAccountOptions([]);
                setToastMessage(`Error fetching GA accounts: ${gaAccountsErrorState.message || 'An error occurred'}`);
                setShowToast(true);
            } else if (fetchedGaAccountsData) {
                setGaAccountOptions(fetchedGaAccountsData.map(acc => ({ value: acc.accountId, label: acc.accountName })) || []);
            } else {
                setGaAccountOptions([]);
            }
        }
    }, [fetchedGaAccountsData, gaAccountsLoadingState, gaAccountsErrorState, setGaAccountOptions, setToastMessage, setShowToast]);

    const triggerGa4Validation = async (cobrandIdToValidate, currentFormValues) => {
        if (!cobrandIdToValidate) {
            setGa4ValidationStatus('idle');
            setGa4OverallMessage('');
            setGa4DetailedErrors([]);
            return;
        }

        setGa4ValidationStatus('loading');
        setGa4OverallMessage('Validating GA4 setup...');
        setGa4DetailedErrors([]);

        try {
            const response = await executeValidateGa4({ params: { cobrandId: cobrandIdToValidate } });
            if (response && response.data && Array.isArray(response.data)) {
                const validationResultsList = response.data;
                let overallValid = true;
                const currentDetailedErrors = [];

                if (validationResultsList.length === 0) {
                    setGa4ValidationStatus('error');
                    setGa4OverallMessage('No GA4 entities found for this cobrand. Consider setting up Google Analytics 4 for enhanced tracking.');
                    setGa4DetailedErrors([]);
                    return;
                }

                validationResultsList.forEach(ga4Result => {
                    const levelPrefix = ga4Result.level ? `${ga4Result.level} GA4` : "GA4";
                    if (ga4Result.steps && ga4Result.steps.length > 0) {
                        ga4Result.steps.forEach(step => {
                            if (!step.passed) {
                                overallValid = false;
                                currentDetailedErrors.push(`${levelPrefix} - ${step.errorMessage || 'Failed'}`);
                            }
                        });
                    }
                });

                if (overallValid) {
                    const levelsValidatedByApi = new Set(validationResultsList.map(item => item.level).filter(Boolean));

                    // Scenario 1: Only 'cobrandtag' was validated by API and it passed.
                    if (levelsValidatedByApi.has('CobrandTag') && levelsValidatedByApi.size === 1) {
                        setGa4ValidationStatus('warning');
                        setGa4OverallMessage('Cobrand Tag GA4 is valid.');
                        setGa4DetailedErrors([
                            'Consider setting up Client-level GA4 for comprehensive tracking.',
                            'Consider setting up Cobrand-level GA4 for comprehensive tracking.'
                        ]);
                    }
                    // Scenario 2: 'client' was validated by API and passed (but not 'cobrand').
                    else if (levelsValidatedByApi.has('Client') && !levelsValidatedByApi.has('Cobrand') && levelsValidatedByApi.size === 2) {
                        setGa4ValidationStatus('warning');
                        setGa4OverallMessage('Client GA4 is valid.');
                        setGa4DetailedErrors(['Consider setting up Cobrand-level GA4 for comprehensive tracking.']);
                    }
                    // Scenario 3: 'cobrand' was validated by API and passed (but not 'client').
                    else if (!levelsValidatedByApi.has('Client') && levelsValidatedByApi.has('Cobrand') && levelsValidatedByApi.size === 2) {
                        setGa4ValidationStatus('warning');
                        setGa4OverallMessage('Cobrand GA4 is valid.');
                        setGa4DetailedErrors(['Consider setting up Client-level GA4 for comprehensive tracking.']);
                    }
                    // Scenario 4: All three levels ('client', 'cobrand', 'cobrandtag' OR client', 'cobrand') were validated by API and passed,
                    // AND the form fields for client and cobrand are filled.
                    else if ((levelsValidatedByApi.has('Client') && levelsValidatedByApi.has('Cobrand') && levelsValidatedByApi.has('CobrandTag') || levelsValidatedByApi.has('Client') && levelsValidatedByApi.has('Cobrand')) &&
                        currentFormValues.gaClientMeasurementId?.trim() && currentFormValues.gaClientPropertyId?.trim() &&
                        currentFormValues.gaCobrandMeasurementId?.trim() && currentFormValues.gaCobrandPropertyId?.trim()) {
                        setGa4ValidationStatus('success');
                        setGa4OverallMessage('GA4 validation passed successfully. All configured levels are valid.');
                        setGa4DetailedErrors([]);
                    }
                    // Fallback Warning: API validation passed, but setup is not one of the specific success/warning scenarios above.
                    else {
                        const missingFormFieldsMessages = [];
                        if (!currentFormValues.gaClientMeasurementId?.trim() || !currentFormValues.gaClientPropertyId?.trim()) {
                            missingFormFieldsMessages.push("Client Measurement ID or Property ID is missing in the form.");
                        }
                        if (!currentFormValues.gaCobrandMeasurementId?.trim() || !currentFormValues.gaCobrandPropertyId?.trim()) {
                            missingFormFieldsMessages.push("Cobrand Measurement ID or Property ID is missing in the form.");
                        }

                        if (missingFormFieldsMessages.length > 0) {
                            setGa4ValidationStatus('warning');
                            setGa4OverallMessage('GA4 validation for configured entities passed, but essential form details are missing.');
                            setGa4DetailedErrors(missingFormFieldsMessages);
                        } else {
                            // API passed, form fields are filled, but not the ideal "all three levels validated" scenario.
                            setGa4ValidationStatus('warning');
                            setGa4OverallMessage('GA4 validation for configured entities passed.');
                            setGa4DetailedErrors(["Review GA4 setup to ensure all intended levels (Client, Cobrand, CobrandTag) are active and validated if desired."]);
                        }
                    }
                } else {
                    setGa4ValidationStatus('error');
                    setGa4OverallMessage('GA4 validation failed. See details below.');
                    setGa4DetailedErrors(currentDetailedErrors.length > 0 ? currentDetailedErrors : ['Validation failed with unspecified errors.']);
                }
            } else {
                setGa4ValidationStatus('error');
                setGa4OverallMessage('GA4 validation failed: Unexpected response from server.');
                setGa4DetailedErrors([]);
            }
        } catch (err) {
            setGa4ValidationStatus('error');
            const apiError = validateGa4ApiError || err;
            const apiErrorMsg = apiError.response?.data?.message || apiError.response?.data || apiError.message || 'An unknown error occurred.';
            setGa4OverallMessage(`GA4 validation request failed: ${apiErrorMsg}`);
            const detailedApiErrors = apiError.response?.data?.errors;
            if (Array.isArray(detailedApiErrors) && detailedApiErrors.length > 0) {
                setGa4DetailedErrors(detailedApiErrors);
            } else if (typeof apiErrorMsg === 'string' && apiErrorMsg !== 'An unknown error occurred.') {
                setGa4DetailedErrors([apiErrorMsg]);
            } else {
                setGa4DetailedErrors(['An error occurred while communicating with the server.']);
            }
            console.error("Error during GA4 validation:", apiError);
        }
    };

    const handleCobrandChange = async (option, setFieldValue, formikValues) => {
        setFieldValue('cobrandId', parseInt(option.value));
        setCobrandId(option.value);
        const selectedCobrandId = option.value;

        setGa4ValidationStatus('idle');
        setGa4OverallMessage('');
        setGa4DetailedErrors([]);

        const result = await fetchCobrandSettings({
            url: `/internal/cobrand/launcher/${selectedCobrandId}`,
        });

        if (!result.error) {
            const settings = result.data;
            const matchedClient = clients.find(c => c.name === settings.clientName);
            setFieldValue('cobrandName', settings.cobrandName);
            setFieldValue('cobrandDescription', settings.cobrandDescription);
            setFieldValue('clientDescription', settings.clientDescription);
            setFieldValue('clientId', matchedClient ? parseInt(matchedClient.clientID) : null);
            setFieldValue('clientName', settings.clientName);
            setFieldValue('platformId', parseInt(settings.platformId));
            const updatedFormValues = { ...formikValues, ...settings };
            triggerGa4Validation(selectedCobrandId, updatedFormValues);
        } else {
            setFieldValue('clientId', null);
            setShowError(true);
        }
    };

    useEffect(() => {
        if (cobrandId) {
            fetchAllGa4Properties();
        }
    }, [cobrandId, fetchAllGa4Properties]);

    useEffect(() => {
        if (allGa4PropertiesData && cobrandId) {
            const selectedCobrandId = String(cobrandId);
            const cobrandDataItem = allGa4PropertiesData.find(item => String(item.cobrandId) === selectedCobrandId);
            setDisplayedGa4Properties([]);
            const levels = ["Client", "Cobrand", "CobrandTag"];
            let ga4Data = [];
            if (cobrandDataItem && Array.isArray(cobrandDataItem.gA4Data)) {
                ga4Data = levels.map(level => {
                    const found = cobrandDataItem.gA4Data.find(d => d.level === level);
                    if (found) return found;
                    return {
                        displayName: null,
                        propertyId: "N/A",
                        measurementId: "N/A",
                        level: level
                    };
                });
            }
            setDisplayedGa4Properties(ga4Data);
        } else {
            setDisplayedGa4Properties([]);
        }
    }, [allGa4PropertiesData, cobrandId]);

    const cobrandsMap = useMemo(() => {
        if (!cobrandsData) return [];
        return cobrandsData.map(c => ({ value: c.cobrandId.toString(), label: c.name }));
    }, [cobrandsData]);

    const cobrandOptions = useMemo(() => {
        return cobrandsMap;
    }, [cobrandsMap]);

    useEffect(() => {
        setEditInitialValues(null);
    }, []);

    const initialValues = {
        gaClientMeasurementId: '',
        gaClientPropertyId: '',
        gaCobrandMeasurementId: '',
        gaCobrandPropertyId: '',
        gaCobrandTagMeasurementId: '',
        gaCobrandTagPropertyId: '',
        ga4Account: null,
        cobrandId: null,
        cobrandDescription: '',
        clientId: null,
        clientName: '',
        clientDescription: '',
        platformId: '',
    };

    const validationSchema = Yup.object().shape({
        cobrandTagDescription: Yup.string().trim().nullable(),
    });

    return (
        <div css={container}>
            <GoogleAnlyticsNotifications
                showToast={showToast}
                setShowToast={setShowToast}
                toastMessage={toastMessage}
                generationStatus={generationStatus}
                setGenerationStatus={setGenerationStatus}
                loadingSetupGa={loadingSetupGa}
            />
            <div className="container-body" >
                <h2>Lookup GA Settings</h2>
                <div>
                    <Formik
                        initialValues={editInitialValues || initialValues}
                        validationSchema={validationSchema}
                        enableReinitialize
                    >
                        {({ setFieldValue, values, touched, errors }) => (
                            <Form>
                                <Label>Select Cobrand</Label>
                                <Select
                                    components={{ MenuList: CustomMenuList }}
                                    options={cobrandOptions}
                                    placeholder={'Select Cobrand'}
                                    name="selectCobrand"
                                    onChange={(option) => {
                                        handleCobrandChange(option, setFieldValue, values);
                                    }}
                                    isSearchable
                                    isLoading={loadingCobrands}
                                    isDisabled={loadingCobrands}
                                    styles={{ container: base => ({ ...base, marginBottom: '10px', width: 400 }), menuList: (base) => ({ ...base, maxHeight: "300px", overflowY: "auto" }) }}
                                />

                                {(ga4ValidationStatus !== 'idle' || validatingGa4) && (
                                    <GoogleAnalyticsMesageBar
                                        ga4ValidationStatus={ga4ValidationStatus}
                                        setGa4ValidationStatus={setGa4ValidationStatus}
                                        ga4OverallMessage={ga4OverallMessage}
                                        setGa4OverallMessage={setGa4OverallMessage}
                                        ga4DetailedErrors={ga4DetailedErrors}
                                        setGa4DetailedErrors={setGa4DetailedErrors}
                                        validatingGa4={validatingGa4}
                                    />
                                )}

                                <div>
                                    <Label required styles={{ root: { fontSize: '14px', fontWeight: 600, color: 'rgb(50, 49, 48)' } }}>GA4 Account</Label>
                                    <Select
                                        components={{ MenuList: CustomMenuList }}
                                        options={gaAccountOptions}
                                        placeholder={gaAccountsLoadingState ? "Loading accounts..." : gaAccountOptions.length === 0 && !gaAccountsErrorState ? "No accounts found" : "Select an account"}
                                        name="ga4Account"
                                        onChange={(option) => {
                                            setFieldValue('ga4Account', option ? option.value : null);
                                        }}
                                        isSearchable
                                        isClearable
                                        isDisabled={gaAccountsLoadingState || !!gaAccountsErrorState}
                                        styles={{ container: base => ({ ...base, marginBottom: '10px', width: 400 }), menuList: (base) => ({ ...base, maxHeight: "300px", overflowY: "auto" }) }}
                                    />
                                    {touched.ga4Account && errors.ga4Account && (
                                        <div style={{ color: 'rgb(164, 38, 44)', fontSize: '12px', marginTop: '4px' }}>
                                            {errors.ga4Account}
                                        </div>
                                    )}
                                </div>


                                {values.cobrandId && (
                                    <GoogleAnalyticsDataPane
                                        cobrandName={values.cobrandName}
                                        isEditModalOpen={isEditModalOpen}
                                        setIsEditModalOpen={setIsEditModalOpen}
                                        loadingAllGa4Properties={loadingAllGa4Properties}
                                        errorAllGa4Properties={errorAllGa4Properties}
                                        displayedGa4Properties={displayedGa4Properties}
                                        ga4Account={values.ga4Account}
                                        setSelectedPropertyForEdit={setSelectedPropertyForEdit}
                                        values={values}
                                        fetchAllGa4Properties={fetchAllGa4Properties}
                                        setupGa={setupGa}
                                        setToastMessage={setToastMessage}
                                        setShowToast={setShowToast}
                                        selectedPropertyLevel={selectedPropertyForEdit ? selectedPropertyForEdit.level : ''}
                                        setDisplayedGa4Properties={setDisplayedGa4Properties}
                                    />
                                )}
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );
}

export default GoogleAnalyticsPage;
